import { api } from '../../core/api/client';
import type { EditionDocumentsDto, EditionDto } from '../../api/generated/types.gen';
import { getEditionDocuments } from '../../api/generated/sdk.gen';
import { handleResponse } from '../../core/api/response-handler';

// Extract types from generated schema
type EditionDTO = EditionDto;

/**
 * Editions API Client
 *
 * Manages program editions for the Ecotic school environmental program.
 * Editions define the program year/cycle (e.g., "2024-2025").
 *
 * @example
 * // Fetch all editions
 * const editions = await editionsApi.getAll();
 *
 * // Find current edition
 * const current = editions.find(e => e.isCurrentEdition);
 */
class EditionsAPI {
    /**
     * Get all program editions
     * @returns Array of editions sorted by start date
     */
    async getAll(): Promise<EditionDTO[]> {
        return api.get('/api/editions');
    }

    /**
     * Get edition by ID
     * @param id - Edition UUID
     * @returns Single edition
     */
    async getById(id: string): Promise<EditionDTO> {
        return api.get(`/api/editions/${id}`);
    }

    /**
     * Create new edition (admin only)
     * @param data - Edition data
     * @returns Created edition
     */
    async create(data: EditionDTO): Promise<EditionDTO> {
        return api.post('/api/editions', data);
    }

    /**
     * Update edition (admin only)
     * @param data - Edition data with id
     * @returns Updated edition
     */
    async update(data: EditionDTO): Promise<EditionDTO> {
        return api.patch('/api/editions', data);
    }

    /**
     * Delete edition (admin only)
     * @param id - Edition UUID
     */
    async delete(id: string): Promise<void> {
        return api.delete(`/api/editions/${id}`);
    }

    /**
     * Linkurile documentelor editiei curente (SE-14) — publica, functioneaza si fara login.
     *
     * ⚠️ Prin SDK-ul GENERAT (`handleResponse`), nu prin `api.get(...)` ca restul clasei: SDK-ul
     * are `ThrowOnError = false` — fara `handleResponse`, un esec ar intoarce un obiect cu
     * `.error` in loc sa arunce, si react-query l-ar trata drept succes (CLAUDE.md, pct. 12).
     */
    async getDocuments(): Promise<EditionDocumentsDto> {
        return handleResponse(await getEditionDocuments());
    }
}

export const editionsApi = new EditionsAPI();
